import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { apiService } from "../services/api";
import {
  ProjectMember,
  PIIValueFinding,
  UploadInspectResponse,
} from "../types/api";
import {
  UploadScopeSelector,
  MetadataScope,
  DownloadScope,
} from "./UploadScopeSelector";
import {
  PIIValueFindingsPanel,
  FindingDecision,
  piiFindingKey,
} from "./PIIValueFindingsPanel";
import { UploadParseOptions } from "../services/api";
import { translateApiError } from "../i18n/apiError";
import { minExpiryDate } from "../utils/datasetExpiry";

export interface UploadParams {
  file: File;
  columns: string[];
  displayName: string;
  metadataScope: MetadataScope;
  downloadScope: DownloadScope;
  downloadUserIds: number[];
  notes: string;
  parseOptions: UploadParseOptions;
  piiFindingsAcknowledged?: boolean;
  piiValueRedactions?: PIIValueFinding[];
  piiDroppedColumns?: string[];
  /** Not shown for data_source uploads — ISO 8601 dataset expiry */
  datasetExpiry?: string;
}

export type UploadKind =
  "admin" | "self" | "data_source" | "admin_data_request";

interface ImportWizardProps {
  projectId: number;
  currentUserId: number;
  defaultDownloadScope?: DownloadScope;
  uploadType?: UploadKind;
  /** The role-scoped inspect endpoint path for this call site. */
  inspectRoute: string;
  // Columns the requester asked for, when fulfilling a data request — marked in
  // the column table so the uploader can see they are delivering the right ones.
  requestedFields?: string[];
  /** Pre-fills the expiry field; leave unset so uploads default to no expiry. */
  defaultExpiryDate?: string;
  onUpload: (params: UploadParams) => Promise<void>;
  onCancel: () => void;
}

type Step = "upload" | "parse" | "columns" | "confirm";

const STEPS: { id: Step }[] = [
  { id: "upload" },
  { id: "parse" },
  { id: "columns" },
  { id: "confirm" },
];

const DELIMITER_PRESETS: { value: string; key: "comma" | "semicolon" | "tab" }[] = [
  { value: ",", key: "comma" },
  { value: ";", key: "semicolon" },
  { value: "\t", key: "tab" },
];

const CheckIcon: React.FC = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const FingerprintIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4" />
    <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
    <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
    <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
    <path d="M8.65 22c.21-.66.45-1.32.57-2" />
    <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
    <path d="M2 16h.01" />
    <path d="M21.8 16c.2-2 .131-5.354 0-6" />
    <path d="M9 6.8a6 6 0 0 1 9 5.2c0 .47 0 1.17-.02 2" />
  </svg>
);

// Explains why the PII scanner flagged a column. Portaled to document.body with
// position:fixed so the tooltip escapes the column table's overflow clip.
const PIIReasonsHint: React.FC<{ reasons: string[] }> = ({ reasons }) => {
  const { t } = useTranslation("importWizard");
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLSpanElement>(null);

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setCoords({ top: rect.top - 6, left: rect.left });
  };
  const hide = () => setCoords(null);

  return (
    <span
      ref={triggerRef}
      className="wiz-pii-hint"
      role="img"
      tabIndex={0}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      // Sits inside the column's <label>; a click here must not toggle the checkbox.
      onClick={(e) => e.preventDefault()}
      aria-label={t("pii.ariaLabel", { reasons: reasons.join("; ") })}
    >
      ?
      {coords &&
        createPortal(
          <div
            className="wiz-pii-tooltip"
            style={{ top: coords.top, left: coords.left }}
          >
            <p className="wiz-pii-tooltip-head">{t("pii.tooltipHeading")}</p>
            <ul className="wiz-pii-tooltip-list">
              {reasons.map((reason, i) => (
                <li key={i}>{reason}</li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </span>
  );
};

export const ImportWizard: React.FC<ImportWizardProps> = ({
  projectId,
  currentUserId,
  defaultDownloadScope = "selected_members",
  uploadType = "admin",
  inspectRoute,
  requestedFields,
  defaultExpiryDate,
  onUpload,
  onCancel,
}) => {
  const { t } = useTranslation(["importWizard", "common"]);
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 2 (parse settings) state
  const [inspect, setInspect] = useState<UploadInspectResponse | null>(null);
  // Sequence number of the newest inspect request, so a slower earlier response
  // cannot clobber newer parse settings.
  const inspectSeqRef = useRef(0);
  const delimiterTimerRef = useRef<number | undefined>(undefined);
  const [maxUploadMB, setMaxUploadMB] = useState(50);
  const [delimiter, setDelimiter] = useState(",");
  const [customDelimiter, setCustomDelimiter] = useState("");
  const [usingCustomDelimiter, setUsingCustomDelimiter] = useState(false);
  const [headerRow, setHeaderRow] = useState(true);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [storeAsWorkbook, setStoreAsWorkbook] = useState(true);

  // Step 3 (columns and access) state
  const [displayName, setDisplayName] = useState("");
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [metadataScope, setMetadataScope] = useState<MetadataScope>("projects");
  const seededMetadataRef = useRef<MetadataScope>("projects");
  const metadataTouchedRef = useRef(false);
  const [downloadScope, setDownloadScope] =
    useState<DownloadScope>(defaultDownloadScope);
  const seededScopeRef = useRef<DownloadScope>(defaultDownloadScope);
  const scopeTouchedRef = useRef(false);
  const [downloadUserIds, setDownloadUserIds] = useState<number[]>([
    currentUserId,
  ]);
  const [notes, setNotes] = useState("");
  const [datasetExpiry, setDatasetExpiry] = useState(defaultExpiryDate || "");
  const [allProjectMembers, setAllProjectMembers] = useState<ProjectMember[]>(
    [],
  );
  const [piiDecisions, setPiiDecisions] = useState<
    Record<string, FindingDecision>
  >({});
  const [piiDroppedColumns, setPiiDroppedColumns] = useState<string[]>([]);
  const [piiAcknowledged, setPiiAcknowledged] = useState(false);

  // Step 4 (confirm) state
  const [confirmPreview, setConfirmPreview] =
    useState<UploadInspectResponse | null>(null);

  // Self-upload is for the uploader's own use only — the backend enforces
  // private/self-only visibility unconditionally, so there's no real choice
  // to offer here (and offering one would be misleading).
  const showScopeSelector = uploadType !== "data_source" && uploadType !== "self";

  useEffect(() => {
    apiService
      .getUploadConfig()
      .then((cfg) => {
        if (cfg.max_file_size_mb > 0) setMaxUploadMB(cfg.max_file_size_mb);
      })
      .catch(() => {});
    return () => {
      if (delimiterTimerRef.current) {
        window.clearTimeout(delimiterTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (uploadType === "data_source") return;
    apiService
      .getProjectDetail(projectId)
      .then((detail) => {
        setAllProjectMembers(detail.members || []);
        const seededDownload: DownloadScope =
          detail.default_researcher_access === "auto_grant"
            ? "project"
            : "none";
        seededScopeRef.current = seededDownload;
        if (!scopeTouchedRef.current) setDownloadScope(seededDownload);
        const seededMetadata = detail.default_metadata_scope;
        seededMetadataRef.current = seededMetadata;
        if (!metadataTouchedRef.current) setMetadataScope(seededMetadata);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, uploadType]);

  useEffect(() => {
    if (
      downloadScope === "selected_members" &&
      !downloadUserIds.includes(currentUserId)
    ) {
      setDownloadUserIds((prev) => [...prev, currentUserId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadScope, currentUserId]);

  const effectiveDelimiter = usingCustomDelimiter ? customDelimiter : delimiter;

  const runInspect = async (
    f: File,
    opts: { delimiter?: string; headerRow?: boolean; sheetName?: string } = {},
  ) => {
    // Parse settings can change while an inspect is in flight; without this the
    // slower response wins and overwrites the delimiter the user just picked.
    const seq = ++inspectSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.uploadInspect(inspectRoute, f, opts);
      if (seq !== inspectSeqRef.current) return null;
      setInspect(result);
      // Parse settings change the headers, so the PII pre-selection is redone too.
      const detected = new Set((result.suspected_pii ?? []).map((s) => s.column));
      setSelectedColumns(result.headers.filter((h) => detected.has(h)));
      if (result.format === "csv" && result.delimiter) {
        const preset = DELIMITER_PRESETS.find(
          (d) => d.value === result.delimiter,
        );
        if (preset) {
          setDelimiter(result.delimiter);
          setUsingCustomDelimiter(false);
        } else {
          setCustomDelimiter(result.delimiter);
          setUsingCustomDelimiter(true);
        }
      }
      if (result.format === "excel" && result.selected_sheet) {
        setSelectedSheet(result.selected_sheet);
      }
      return result;
    } catch (err) {
      if (seq === inspectSeqRef.current) {
        setError(err instanceof Error ? err.message : t("errors.readFailed"));
      }
      return null;
    } finally {
      if (seq === inspectSeqRef.current) setLoading(false);
    }
  };

  const handleFileSelect = async (f: File) => {
    if (f.size > maxUploadMB * 1024 * 1024) {
      setError(t("errors.fileTooLarge", { maxMB: maxUploadMB }));
      return;
    }
    setFile(f);
    const result = await runInspect(f);
    if (result) setStep("parse");
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  // Re-inspect whenever the parse settings change, so the preview + PII
  // pre-marking always reflect exactly what will be committed.
  const reinspectParse = async (overrides: {
    delimiter?: string;
    headerRow?: boolean;
    sheetName?: string;
  }) => {
    if (!file) return;
    await runInspect(file, {
      delimiter:
        overrides.delimiter ??
        (inspect?.format === "csv" ? effectiveDelimiter : undefined),
      headerRow: overrides.headerRow ?? headerRow,
      sheetName: overrides.sheetName ?? selectedSheet,
    });
  };

  const handleDelimiterPreset = (value: string) => {
    setDelimiter(value);
    setUsingCustomDelimiter(false);
    reinspectParse({ delimiter: value });
  };
  const handleCustomDelimiter = (value: string) => {
    setCustomDelimiter(value);
    setUsingCustomDelimiter(true);
    if (delimiterTimerRef.current) {
      window.clearTimeout(delimiterTimerRef.current);
    }
    if (!value) return;
    // Re-inspecting re-uploads and re-scans the whole file, so wait for a pause
    // in typing rather than firing on every keystroke.
    delimiterTimerRef.current = window.setTimeout(
      () => reinspectParse({ delimiter: value }),
      400,
    );
  };
  const handleHeaderRowToggle = (checked: boolean) => {
    setHeaderRow(checked);
    reinspectParse({ headerRow: checked });
  };
  const handleSheetSelect = (name: string) => {
    setSelectedSheet(name);
    reinspectParse({ sheetName: name });
  };

  const goToColumns = () => {
    if (!inspect || selectedColumns.length === 0) return;
    if (!displayName && file) {
      setDisplayName(file.name.replace(/\.[^.]+$/, ""));
    }
    setPiiDecisions({});
    setPiiDroppedColumns([]);
    setPiiAcknowledged(false);
    setStep("columns");
  };

  const toggleColumn = (col: string) =>
    setSelectedColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col],
    );
  const handlePseudonymizeColumn = (col: string) =>
    setSelectedColumns((prev) => (prev.includes(col) ? prev : [...prev, col]));

  const handleMetadataScopeChange = (scope: MetadataScope) => {
    metadataTouchedRef.current = true;
    setMetadataScope(scope);
  };
  const handleDownloadScopeChange = (scope: DownloadScope) => {
    scopeTouchedRef.current = true;
    setDownloadScope(scope);
  };

  const suspectedPII: Record<string, string[]> = {};
  for (const s of inspect?.suspected_pii ?? [])
    suspectedPII[s.column] = s.reasons;
  const piiValueFindings = inspect?.pii_value_findings ?? [];
  const unhandledFindings = piiValueFindings.filter(
    (f) =>
      !selectedColumns.includes(f.column) &&
      !piiDroppedColumns.includes(f.column),
  );
  const piiBlocked = unhandledFindings.length > 0 && !piiAcknowledged;

  const goToConfirm = async () => {
    if (!file || selectedColumns.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.uploadInspect(inspectRoute, file, {
        delimiter: inspect?.format === "csv" ? effectiveDelimiter : undefined,
        headerRow,
        sheetName: selectedSheet,
        pseudonymizeColumns: selectedColumns,
        dropColumns: piiDroppedColumns,
      });
      setConfirmPreview(result);
      setStep("confirm");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.previewFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!file || selectedColumns.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const droppedSet = new Set(piiDroppedColumns);
      const redactions = piiValueFindings.filter(
        (f) =>
          piiDecisions[piiFindingKey(f)] === "redact" &&
          !selectedColumns.includes(f.column) &&
          !droppedSet.has(f.column),
      );
      const columnsForUpload = selectedColumns.filter(
        (c) => !droppedSet.has(c),
      );
      await onUpload({
        file,
        columns: columnsForUpload,
        displayName: displayName || file.name.replace(/\.[^.]+$/, ""),
        metadataScope,
        downloadScope,
        downloadUserIds:
          downloadScope === "selected_members" ? downloadUserIds : [],
        notes,
        parseOptions: {
          delimiter: inspect?.format === "csv" ? effectiveDelimiter : undefined,
          headerRow,
          sheetName: selectedSheet || undefined,
          storeAsWorkbook:
            inspect?.format === "excel" ? storeAsWorkbook : undefined,
        },
        piiFindingsAcknowledged: piiAcknowledged,
        piiValueRedactions: redactions,
        piiDroppedColumns,
        datasetExpiry: datasetExpiry
          ? new Date(datasetExpiry).toISOString()
          : undefined,
      });
    } catch (err) {
      // Coded errors (e.g. an expiry in the past) get the translated message;
      // uncoded ones keep the backend's text, which is more specific than generic.
      setError(
        (err as { code?: string }).code
          ? translateApiError(t, err)
          : err instanceof Error
            ? err.message
            : t("errors.uploadFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const stepIndex = STEPS.findIndex((s) => s.id === step);
  // Pseudonymized columns are KEPT — their values are replaced with a hash,
  // but the column itself still exists in the stored/downloaded file, same
  // name, same position. Only piiDroppedColumns are actually removed from
  // the file. These two lists used to be conflated in the confirm-step
  // summary ("Removed: X, Y, Z"), which was simply wrong for anything but a
  // dropped column.
  const droppedColumns = confirmPreview
    ? confirmPreview.headers.filter((h) => piiDroppedColumns.includes(h))
    : [];
  const pseudonymizedColumns = confirmPreview
    ? confirmPreview.headers.filter(
        (h) => selectedColumns.includes(h) && !piiDroppedColumns.includes(h),
      )
    : [];
  // preview_transformed already has exactly this shape: every non-dropped
  // column in its original order, with pseudonymized columns' values
  // replaced in place — so it lines up 1:1 with this filtered header list.
  const confirmHeaders = confirmPreview
    ? confirmPreview.headers.filter((h) => !piiDroppedColumns.includes(h))
    : [];

  return (
    <div>
      <style>{`
        .wiz-steps { display: flex; align-items: center; gap: 6px; margin-bottom: 20px; }
        .wiz-step { display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--text-muted); }
        .wiz-step.active { color: var(--text-primary); font-weight: 500; }
        .wiz-step.done { color: var(--text-muted); }
        .wiz-dot { width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; flex-shrink: 0; }
        .wiz-dot.pending { border: 1px solid var(--border-strong); color: var(--text-muted); }
        .wiz-dot.active { background: var(--fill-primary); color: var(--on-primary); }
        .wiz-dot.done { background: var(--fill-primary); color: var(--on-primary); }
        .wiz-line { flex: 1; height: 1px; background: var(--border); }
        .wiz-dropzone { padding: 32px; text-align: center; border: 2px dashed var(--border-strong); border-radius: var(--radius); cursor: pointer; }
        .wiz-dropzone.active { border-color: var(--border-accent); background: var(--bg-accent-muted); }
        .wiz-file-bar { display: flex; justify-content: space-between; align-items: center; background: var(--surface-1); border-radius: var(--radius); padding: 10px 14px; margin-bottom: 20px; }
        .wiz-section-label { font-size: 13px; font-weight: 500; color: var(--text-secondary); margin: 0 0 8px; }
        .wiz-delim-group { display: flex; gap: 6px; align-items: stretch; }
        .wiz-delim-btn { flex: 1; padding: 7px 0; font-size: 13px; border-radius: var(--radius); border: 0.5px solid var(--border-strong); background: var(--surface-2); color: var(--text-secondary); cursor: pointer; font-family: inherit; white-space: nowrap; }
        .wiz-delim-btn.active { background: var(--fill-primary); color: var(--on-primary); border-color: var(--fill-primary); }
        .wiz-delim-char {
          flex: 0 0 44px; width: 44px; padding: 7px 0; text-align: center;
          font-size: 13px; font-family: var(--font-mono, ui-monospace, monospace);
          border-radius: var(--radius); border: 0.5px solid var(--border-accent);
          background: var(--surface-2); color: var(--text-primary); box-sizing: border-box;
        }
        .wiz-delim-char:focus { outline: none; box-shadow: 0 0 0 2px var(--bg-accent); }
        .wiz-input, .wiz-textarea {
          width: 100%; padding: 8px 12px; font-size: 14px; font-family: inherit;
          border-radius: var(--radius); border: 1px solid var(--border-strong);
          background: var(--surface-2); color: var(--text-primary); box-sizing: border-box;
        }
        .wiz-textarea { resize: vertical; }
        .wiz-input:focus, .wiz-textarea:focus {
          outline: none; border-color: var(--border-accent); box-shadow: 0 0 0 2px var(--bg-accent);
        }
        .wiz-input::placeholder, .wiz-textarea::placeholder { color: var(--text-muted); }
        .wiz-table-wrap { border: 0.5px solid var(--border); border-radius: var(--radius); overflow: auto; }
        .wiz-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .wiz-table th { text-align: left; padding: 8px 10px; color: var(--text-muted); font-weight: 500; background: var(--surface-1); border-bottom: 0.5px solid var(--border); }
        .wiz-table td { padding: 8px 10px; border-bottom: 0.5px solid var(--border); font-family: var(--font-mono, ui-monospace, monospace); }
        .wiz-table tr:last-child td { border-bottom: none; }
        .wiz-caption { font-size: 12px; color: var(--text-muted); margin: 8px 0 0; }
        .wiz-sheet-list { background: var(--surface-1); border-right: 0.5px solid var(--border); padding: 0.6rem; }
        .wiz-sheet-row { display: flex; flex-direction: column; gap: 2px; padding: 9px 10px; border-radius: var(--radius); cursor: pointer; }
        .wiz-sheet-row.active { background: var(--bg-accent-muted); }
        .wiz-sheet-name { font-size: 13px; font-weight: 500; margin: 0; }
        .wiz-sheet-row.active .wiz-sheet-name { color: var(--text-accent); }
        .wiz-sheet-meta { font-size: 11px; color: var(--text-muted); margin: 0; }
        .wiz-warning-banner { background: var(--bg-warning); border-radius: var(--radius); padding: 10px 14px; margin-bottom: 14px; font-size: 13px; color: var(--text-warning); }
        .wiz-col-picker { margin-top: 24px; }
        .wiz-col-picker-head { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin-bottom: 8px; }
        .wiz-col-picker-head .wiz-section-label { margin: 0; }
        .wiz-col-count { font-size: 12px; color: var(--text-muted); }
        .wiz-col-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 8px; }
        .wiz-col-item {
          display: flex; align-items: center; gap: 9px; min-width: 0;
          padding: 9px 11px; border-radius: var(--radius);
          border: 1px solid var(--border); background: var(--surface-2);
          cursor: pointer; user-select: none;
          transition: border-color 0.12s, background 0.12s;
        }
        .wiz-col-item:hover { border-color: var(--border-strong); }
        .wiz-col-item.selected { border-color: var(--border-accent); background: var(--bg-accent-muted); }
        .wiz-col-item input { margin: 0; flex-shrink: 0; cursor: pointer; accent-color: var(--fill-primary); }
        .wiz-col-name {
          flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          font-size: 13px; font-family: var(--font-mono, ui-monospace, monospace); color: var(--text-primary);
        }
        .wiz-col-item:not(.selected) .wiz-col-name { color: var(--text-secondary); }
        .wiz-col-requested { width: 6px; height: 6px; border-radius: 50%; background: var(--text-accent); flex-shrink: 0; }
        .wiz-pii-hint {
          width: 16px; height: 16px; flex-shrink: 0; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 10px; font-weight: 600; line-height: 1; cursor: help;
          background: var(--bg-danger); color: var(--text-danger);
        }
        .wiz-pii-hint:focus-visible { outline: 2px solid var(--border-accent); outline-offset: 1px; }
        .wiz-pii-tooltip {
          position: fixed;
          transform: translateY(-100%);
          z-index: 10000;
          min-width: 210px;
          max-width: 320px;
          padding: 8px 10px;
          border-radius: var(--radius);
          background: var(--text-primary);
          color: #fff;
          font-size: 11px;
          line-height: 1.45;
          text-align: left;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
          pointer-events: none;
        }
        .wiz-pii-tooltip-head { margin: 0 0 4px; font-weight: 600; }
        .wiz-pii-tooltip-list { margin: 0; padding-left: 14px; }
        .wiz-actions { display: flex; justify-content: flex-end; align-items: center; gap: 10px; margin-top: 24px; flex-wrap: wrap; }
        /* .wiz-btn-primary is used on its own, not alongside .wiz-btn, so it
           needs the full box — without padding/font-size of its own the label
           ran into the button edges. inline-flex keeps it content-sized
           instead of stretching inside the flex row. */
        .wiz-btn, .wiz-btn-primary {
          padding: 8px 16px; font-size: 14px; line-height: 1.2;
          border-radius: var(--radius); border: 0.5px solid var(--border-strong);
          background: var(--surface-2); color: var(--text-primary);
          cursor: pointer; font-family: inherit; white-space: nowrap;
          display: inline-flex; align-items: center; justify-content: center; gap: 6px;
        }
        .wiz-btn-primary { background: var(--fill-primary); color: var(--on-primary); border-color: transparent; font-weight: 500; }
        .wiz-btn-primary:disabled, .wiz-btn:disabled {
          background: var(--fill-disabled); color: var(--text-disabled);
          border-color: var(--border-disabled); cursor: not-allowed;
        }
        .wiz-summary-cards { display: flex; gap: 10px; margin-bottom: 20px; }
        .wiz-summary-card { flex: 1; border-radius: var(--radius); padding: 10px 14px; }
        .wiz-summary-card.removed { background: var(--bg-danger); }
        .wiz-summary-card.added { background: var(--bg-success); }
        .wiz-summary-label { font-size: 12px; margin: 0 0 2px; }
        .wiz-summary-value { font-size: 14px; font-weight: 500; margin: 0; }
        .wiz-access-summary { display: flex; justify-content: space-between; align-items: center; padding: 11px 14px; background: var(--surface-1); border-radius: var(--radius); margin-bottom: 24px; font-size: 13px; color: var(--text-secondary); }
        .wiz-error { margin-bottom: 12px; padding: 10px 14px; background: var(--bg-danger); color: var(--text-danger); border-radius: var(--radius); font-size: 13px; }
      `}</style>

      {error && <div className="wiz-error">{error}</div>}

      <div className="wiz-steps">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <span
              className={`wiz-step ${i === stepIndex ? "active" : i < stepIndex ? "done" : ""}`}
            >
              <span
                className={`wiz-dot ${i < stepIndex ? "done" : i === stepIndex ? "active" : "pending"}`}
              >
                {i < stepIndex ? <CheckIcon /> : i + 1}
              </span>
              {t(`steps.${s.id}`)}
            </span>
            {i < STEPS.length - 1 && <span className="wiz-line" />}
          </React.Fragment>
        ))}
      </div>

      {step === "upload" && (
        <div
          className={`wiz-dropzone ${dragActive ? "active" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() =>
            document.getElementById(`wiz-file-input-${projectId}`)?.click()
          }
        >
          {loading ? (
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: "14px",
                margin: 0,
              }}
            >
              {t("upload.readingFile")}
            </p>
          ) : (
            <>
              <p
                style={{
                  fontWeight: 500,
                  color: "var(--text-primary)",
                  margin: "0 0 4px",
                }}
              >
                {t("upload.clickOrDrag")}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "var(--text-muted)",
                  margin: 0,
                }}
              >
                {t("upload.fileTypesHint")}
              </p>
            </>
          )}
          <input
            id={`wiz-file-input-${projectId}`}
            type="file"
            accept=".csv,.tsv,.txt,.xlsx,.sas7bdat"
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
            }}
          />
        </div>
      )}

      {step === "parse" && file && inspect && (
        <>
          <div className="wiz-file-bar">
            <span style={{ fontSize: "14px", fontWeight: 500 }}>
              {file.name}
            </span>
            <span style={{ fontSize: "13px", color: "var(--text-muted)" }}>
              {(file.size / 1024).toFixed(1)} KB
            </span>
          </div>

          {inspect.format === "csv" ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
                marginBottom: "20px",
              }}
            >
              <div>
                <p className="wiz-section-label">{t("parse.delimiterLabel")}</p>
                <div className="wiz-delim-group">
                  {DELIMITER_PRESETS.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      className={`wiz-delim-btn ${!usingCustomDelimiter && delimiter === d.value ? "active" : ""}`}
                      onClick={() => handleDelimiterPreset(d.value)}
                    >
                      {t(`delimiters.${d.key}`)}
                    </button>
                  ))}
                  {/* "Other" and its character box are one control: the box
                      sits to the right of the button rather than dropping
                      below the row. */}
                  <button
                    type="button"
                    className={`wiz-delim-btn ${usingCustomDelimiter ? "active" : ""}`}
                    onClick={() => setUsingCustomDelimiter(true)}
                  >
                    {t("delimiters.other")}
                  </button>
                  {usingCustomDelimiter && (
                    <input
                      type="text"
                      className="wiz-delim-char"
                      maxLength={1}
                      value={customDelimiter}
                      onChange={(e) => handleCustomDelimiter(e.target.value)}
                      placeholder="|"
                      aria-label={t("parse.customDelimiterAriaLabel")}
                      title={t("parse.customDelimiterTitle")}
                      autoFocus
                    />
                  )}
                </div>
              </div>
              <div>
                <p className="wiz-section-label">{t("parse.optionsLabel")}</p>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={headerRow}
                    onChange={(e) => handleHeaderRowToggle(e.target.checked)}
                  />
                  {t("parse.headerRowLabel")}
                </label>
              </div>
            </div>
          ) : inspect.format === "excel" ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "220px 1fr",
                border: "0.5px solid var(--border)",
                borderRadius: "var(--radius)",
                overflow: "hidden",
                minHeight: "220px",
                marginBottom: "20px",
              }}
            >
              <div className="wiz-sheet-list">
                <p
                  style={{
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    margin: "0 0 8px",
                    padding: "0 4px",
                  }}
                >
                  {t("parse.sheetsListLabel")}
                </p>
                {(inspect.sheets ?? []).map((sheet) => (
                  <div
                    key={sheet.name}
                    className={`wiz-sheet-row ${selectedSheet === sheet.name ? "active" : ""}`}
                    onClick={() => handleSheetSelect(sheet.name)}
                  >
                    <p className="wiz-sheet-name">{sheet.name}</p>
                    <p className="wiz-sheet-meta">
                      {t("parse.sheetMeta", {
                        rows: sheet.rows,
                        columns: sheet.columns,
                      })}
                    </p>
                  </div>
                ))}
              </div>
              <div style={{ padding: "0.9rem 1.1rem" }}>
                <div style={{ marginBottom: "14px" }}>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "var(--text-muted)",
                      margin: "0 0 6px",
                    }}
                  >
                    {t("parse.storeAsLabel")}
                  </p>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      fontSize: "13px",
                      marginBottom: "6px",
                    }}
                  >
                    <input
                      type="radio"
                      name="excel-storage-mode"
                      checked={storeAsWorkbook}
                      onChange={() => setStoreAsWorkbook(true)}
                      style={{ marginTop: "3px" }}
                    />
                    <span>{t("parse.storeAsWorkbookOption")}</span>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      fontSize: "13px",
                    }}
                  >
                    <input
                      type="radio"
                      name="excel-storage-mode"
                      checked={!storeAsWorkbook}
                      onChange={() => setStoreAsWorkbook(false)}
                      style={{ marginTop: "3px" }}
                    />
                    <span>{t("parse.storeAsCsvOption")}</span>
                  </label>
                </div>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    marginBottom: "10px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={headerRow}
                    onChange={(e) => handleHeaderRowToggle(e.target.checked)}
                  />
                  {t("parse.headerRowLabel")}
                </label>
                <PreviewTable inspect={inspect} />
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: "20px" }}>
              <p
                style={{
                  fontSize: "13px",
                  color: "var(--text-muted)",
                  margin: "0 0 12px",
                }}
              >
                {t("parse.sasHint")}
              </p>
              <PreviewTable inspect={inspect} />
            </div>
          )}

          {inspect.format === "csv" && <PreviewTable inspect={inspect} />}

          <div className="wiz-col-picker">
            <div className="wiz-col-picker-head">
              <p className="wiz-section-label">{t("columns.pickerLabel")}</p>
              <span className="wiz-col-count">
                {t("columns.deidentifiedCaption", {
                  selected: selectedColumns.length,
                  total: inspect.headers.length,
                })}
              </span>
            </div>
            {Object.keys(suspectedPII).length > 0 && (
              <div className="wiz-warning-banner">
                {t("columns.piiPreselectedBanner", {
                  count: Object.keys(suspectedPII).length,
                })}
              </div>
            )}
            <div className="wiz-col-grid">
              {inspect.headers.map((col) => {
                const selected = selectedColumns.includes(col);
                return (
                  <label
                    key={col}
                    className={`wiz-col-item ${selected ? "selected" : ""}`}
                    title={col}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleColumn(col)}
                    />
                    <span className="wiz-col-name">{col}</span>
                    {requestedFields?.includes(col) && (
                      <span
                        className="wiz-col-requested"
                        title={t("columns.requestedBadgeTitle")}
                        aria-label={t("columns.requestedBadgeTitle")}
                      />
                    )}
                    {suspectedPII[col] && (
                      <PIIReasonsHint reasons={suspectedPII[col]} />
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="wiz-actions">
            <button className="wiz-btn" onClick={() => setStep("upload")}>
              {t("common:actions.back")}
            </button>
            <button
              className="wiz-btn-primary"
              onClick={goToColumns}
              disabled={loading || selectedColumns.length === 0}
              title={
                selectedColumns.length === 0
                  ? t("columns.noneSelectedTitle")
                  : undefined
              }
            >
              {t("common:actions.continue")}
            </button>
          </div>
        </>
      )}

      {step === "columns" && inspect && (
        <>
          <div style={{ marginBottom: "20px" }}>
            <p className="wiz-section-label">{t("columns.datasetNameLabel")}</p>
            <input
              type="text"
              className="wiz-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t("columns.datasetNamePlaceholder")}
            />
          </div>

          {uploadType !== "data_source" &&
            uploadType !== "admin_data_request" && (
              <div style={{ marginBottom: "20px" }}>
                <p className="wiz-section-label">
                  {t("columns.descriptionLabel")}
                </p>
                <textarea
                  className="wiz-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder={t("columns.descriptionPlaceholder")}
                />
              </div>
            )}

          {showScopeSelector && (
            <UploadScopeSelector
              uniqueId={`wizard-${projectId}`}
              metadataScope={metadataScope}
              downloadScope={downloadScope}
              downloadUserIds={downloadUserIds}
              allProjectMembers={allProjectMembers}
              lockedDownloadUserIds={[currentUserId]}
              onMetadataScopeChange={handleMetadataScopeChange}
              onDownloadScopeChange={handleDownloadScopeChange}
              onDownloadUserIdsChange={setDownloadUserIds}
            />
          )}

          {uploadType === "self" && (
            <p
              className="wiz-caption"
              style={{
                marginBottom: "20px",
                padding: "10px 12px",
                background: "var(--surface-1)",
                borderRadius: "var(--radius)",
              }}
            >
              {t("columns.selfUploadNotice")}
            </p>
          )}

          {uploadType !== "data_source" && (
            <div style={{ marginBottom: "20px" }}>
              <p className="wiz-section-label">{t("columns.datasetExpiryLabel")}</p>
              <p className="wiz-caption">{t("columns.datasetExpiryHint")}</p>
              <input
                type="date"
                className="wiz-input"
                style={{ width: "auto" }}
                value={datasetExpiry}
                min={minExpiryDate()}
                onChange={(e) => setDatasetExpiry(e.target.value)}
              />
            </div>
          )}

          <PIIValueFindingsPanel
            findings={piiValueFindings}
            pseudonymizedColumns={selectedColumns}
            droppedColumns={piiDroppedColumns}
            decisions={piiDecisions}
            onDecide={(key, decision) =>
              setPiiDecisions((prev) => ({ ...prev, [key]: decision }))
            }
            onDecideAllInColumn={(col, decision) =>
              setPiiDecisions((prev) => {
                const next = { ...prev };
                for (const f of piiValueFindings) {
                  if (f.column === col) next[piiFindingKey(f)] = decision;
                }
                return next;
              })
            }
            onPseudonymizeColumn={handlePseudonymizeColumn}
            onDropColumn={(col) =>
              setPiiDroppedColumns((prev) =>
                prev.includes(col) ? prev : [...prev, col],
              )
            }
            onRestoreColumn={(col) =>
              setPiiDroppedColumns((prev) => prev.filter((c) => c !== col))
            }
            acknowledged={piiAcknowledged}
            onAcknowledgeChange={setPiiAcknowledged}
          />

          <div className="wiz-actions">
            <button className="wiz-btn" onClick={() => setStep("parse")}>
              {t("common:actions.back")}
            </button>
            <button
              className="wiz-btn-primary"
              onClick={goToConfirm}
              disabled={loading || selectedColumns.length === 0 || piiBlocked}
              title={piiBlocked ? t("columns.piiBlockedTitle") : undefined}
            >
              {t("columns.reviewAndConfirmButton")}
            </button>
          </div>
        </>
      )}

      {step === "confirm" && confirmPreview && (
        <>
          <div className="wiz-summary-cards">
            {/* Dropping is only offered by the PII findings panel, so an empty card would point at an option the user may never have seen. */}
            {droppedColumns.length > 0 && (
              <div className="wiz-summary-card removed">
                <p
                  className="wiz-summary-label"
                  style={{ color: "var(--text-danger)" }}
                >
                  {t("confirm.removedLabel")}
                </p>
                <p
                  className="wiz-summary-value"
                  style={{ color: "var(--text-danger)" }}
                >
                  {droppedColumns.join(", ")}
                </p>
              </div>
            )}
            <div className="wiz-summary-card added">
              <p
                className="wiz-summary-label"
                style={{ color: "var(--text-success)" }}
              >
                {t("confirm.pseudonymizedLabel")}
              </p>
              <p
                className="wiz-summary-value"
                style={{ color: "var(--text-success)" }}
              >
                {pseudonymizedColumns.length > 0
                  ? pseudonymizedColumns.join(", ")
                  : "—"}
              </p>
            </div>
          </div>
          <p className="wiz-caption" style={{ marginBottom: "16px" }}>
            {t(
              droppedColumns.length > 0
                ? "confirm.explanationCaption"
                : "confirm.explanationCaptionNoDrops",
            )}
          </p>

          <p className="wiz-section-label">{t("confirm.previewHeading")}</p>
          <div className="wiz-table-wrap">
            <table className="wiz-table">
              <thead>
                <tr>
                  {confirmHeaders.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(confirmPreview.preview_transformed ?? []).map((row, ri) => (
                  <tr key={ri}>
                    {confirmHeaders.map((h, ci) => (
                      <td
                        key={h}
                        style={{
                          color: selectedColumns.includes(h)
                            ? "var(--text-success)"
                            : "var(--text-secondary)",
                          fontFamily: "inherit",
                        }}
                      >
                        {row[ci]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="wiz-caption" style={{ marginBottom: "20px" }}>
            {t("confirm.columnsRowsCaption", {
              columns: confirmHeaders.length,
              shown: confirmPreview.sample_rows.length,
              total: confirmPreview.row_count,
            })}
          </p>

          {uploadType === "self" && (
            <div className="wiz-access-summary">
              <span>
                {t("confirm.visibleToOnlyYou")}{" "}
                <strong>{t("confirm.onlyYou")}</strong>
              </span>
            </div>
          )}

          {showScopeSelector && (
            <div className="wiz-access-summary">
              <span>
                {t("confirm.visibleTo")}{" "}
                <strong>{t(`confirm.metadataLabel.${metadataScope}`)}</strong> ·{" "}
                {t("confirm.downloadableBy")}{" "}
                <strong>{t(`confirm.downloadLabel.${downloadScope}`)}</strong>
              </span>
              <button
                className="wiz-btn"
                style={{ padding: "5px 10px", fontSize: "12px" }}
                onClick={() => setStep("columns")}
              >
                {t("common:actions.edit")}
              </button>
            </div>
          )}

          <div className="wiz-actions">
            <button
              className="wiz-btn"
              onClick={() => setStep("columns")}
              disabled={loading}
            >
              {t("common:actions.back")}
            </button>
            <button
              className="wiz-btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              <FingerprintIcon />
              {loading
                ? t("confirm.uploadingButton")
                : t("confirm.uploadAndDeidentifyButton")}
            </button>
            <button className="wiz-btn" onClick={onCancel} disabled={loading}>
              {t("common:actions.cancel")}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const PreviewTable: React.FC<{ inspect: UploadInspectResponse }> = ({
  inspect,
}) => {
  const { t } = useTranslation("importWizard");
  return (
    <>
      <p className="wiz-section-label">{t("preview.label")}</p>
      <div className="wiz-table-wrap">
        <table className="wiz-table">
          <thead>
            <tr>
              {inspect.headers.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {inspect.sample_rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="wiz-caption">
        {t("preview.caption", {
          columnCount: inspect.column_count,
          shown: inspect.sample_rows.length,
          total: inspect.row_count,
        })}
      </p>
    </>
  );
};
